import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import {
  SERIES_BY_SLUG,
  formatPublishDate,
  type ActivationSeries,
} from "@/lib/activation-series";

type A = Database["public"]["Tables"]["activations"]["Row"];

export const Route = createFileRoute("/activations/series/$series")({
  loader: ({ params }) => {
    const series = SERIES_BY_SLUG[params.series as ActivationSeries];
    if (!series) throw notFound();
    return { series };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Series not found · KAfrequency" }, { name: "robots", content: "noindex" }] };
    const s = loaderData.series;
    return {
      meta: [
        { title: `${s.label} · KAfrequency Series ${s.number}` },
        { name: "description", content: s.tagline },
        { property: "og:title", content: `${s.label} · KAfrequency` },
        { property: "og:description", content: s.tagline },
      ],
      links: [{ rel: "canonical", href: `/activations/series/${s.slug}` }],
    };
  },
  component: SeriesPage,
});

function SeriesPage() {
  const { series } = Route.useLoaderData();
  const [rows, setRows] = useState<A[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("activations")
        .select("id, slug, title, subtitle, intention, activation_number, hero_image_url, publish_at, series, slot")
        .eq("series", series.slug)
        .order("publish_at", { ascending: false, nullsFirst: false });
      if (cancelled) return;
      setRows((data ?? []) as A[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [series.slug]);

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <Link to="/activations" className="text-xs uppercase tracking-[0.3em] text-gold">← Activations</Link>
          <p className="mt-6 text-xs uppercase tracking-[0.3em] text-gold">Series {series.number} of 10</p>
          <h1 className="mt-2 font-serif text-5xl sm:text-6xl">{series.label}</h1>
          <p className="mt-4 max-w-2xl font-serif text-lg italic text-ivory/90">"{series.tagline}"</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border hairline bg-charcoal p-12 text-center">
            <p className="font-serif text-2xl text-gold-luminous">No activations in this series yet.</p>
            <p className="mt-2 text-sm text-muted-foreground">The archive grows daily. Return soon.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((a) => (
              <Link
                key={a.id}
                to="/activations/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col overflow-hidden rounded-xl border hairline bg-charcoal transition-all hover:border-gold hover:shadow-gold"
              >
                <ReleaseCover coverUrl={a.hero_image_url} label={a.title} aspect="square" alt={a.title} />
                <div className="flex flex-1 flex-col p-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold">
                    {a.activation_number ? `№ ${a.activation_number}` : "Draft"}
                  </p>
                  <h2 className="mt-3 font-serif text-2xl group-hover:text-gold-luminous">{a.title}</h2>
                  {a.subtitle && <p className="mt-1 text-sm text-muted-foreground">{a.subtitle}</p>}
                  <p className="mt-3 flex-1 text-sm text-muted-foreground">{a.intention}</p>
                  {a.publish_at && <p className="mt-4 text-xs text-muted-foreground">{formatPublishDate(a.publish_at)}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
